import { CheckCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { Table as AntTable, Button, Card, Flex, Tag, Typography } from 'antd';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

import { PayrollShiftListItemDto } from '@clh/api-client';
import { HumlaDateTime, toHourAndMinuteDisplay } from '@clh/util';

import { useApiClient } from '../hooks/use-api-client';
import Spinner from '../spinner';

import usePayPeriodContext from './pay-period-context';

const { Text } = Typography;

function ShiftSummary({ shifts }: { shifts: PayrollShiftListItemDto[] }) {
    const totalShifts = shifts.length;
    const shiftsAwaitingTimeSubmission = shifts.filter(
        (shift) =>
            !shift.minutesWorked &&
            !shift.onCallShift?.minutesWorked &&
            !shift.didNotWork
    ).length;

    return (
        <Card size="small" style={{ marginBottom: 16 }}>
            <Flex justify="space-between" align="center">
                <Typography.Title level={5} style={{ margin: 0, padding: 0 }}>
                    Shift Summary
                </Typography.Title>
                <Flex gap={24}>
                    {shiftsAwaitingTimeSubmission > 0 && (
                        <div style={{ textAlign: 'center' }}>
                            <div
                                style={{
                                    fontSize: '20px',
                                    fontWeight: 'bold',
                                    color: 'red',
                                }}
                            >
                                {shiftsAwaitingTimeSubmission}
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Awaiting Time Submission
                            </Text>
                        </div>
                    )}
                    <div style={{ textAlign: 'center' }}>
                        <div
                            style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: '#52c41a',
                            }}
                        >
                            {totalShifts}
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Assigned & Active Shifts
                        </Text>
                    </div>
                </Flex>
            </Flex>
        </Card>
    );
}

export default function PayrollShifts() {
    const api = useApiClient();
    const [shifts, setShifts] = useState<PayrollShiftListItemDto[]>([]);
    const [loading, setLoading] = useState(false);

    const { startDate: payPeriodStart, endDate: payPeriodEnd } =
        usePayPeriodContext();

    const fetchShifts = async () => {
        if (!api) return;

        setLoading(true);
        try {
            const response = await api.payrollControllerListPayrollShifts({
                after: payPeriodStart,
                before: payPeriodEnd,
            });

            setShifts(response);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchShifts();
    }, [payPeriodStart.toISOString(), payPeriodEnd.toISOString()]);

    if (!api) {
        return <Spinner />;
    }

    if (loading) {
        return <Spinner />;
    }

    const payPeriodForShift = (shift: PayrollShiftListItemDto) =>
        shift.payPeriods?.find(
            (pp) =>
                pp.canonicalStart.getTime() === payPeriodStart.getTime() &&
                pp.canonicalEnd.getTime() === payPeriodEnd.getTime()
        );

    const columns = [
        {
            title: 'Facility',
            dataIndex: 'facility',
            render: (facility: any, shift: PayrollShiftListItemDto) => (
                <Button
                    type="link"
                    target="_blank"
                    href={`/shifts/${shift.id}`}
                    icon={<LinkOutlined />}
                    style={{ padding: 0, height: 'auto' }}
                >
                    {facility.name}
                </Button>
            ),
            ellipsis: true,
        },
        {
            dataIndex: 'shiftType',
            render: (shiftType: string, shift: PayrollShiftListItemDto) => (
                <>
                    <Tag>{shiftType.toLowerCase()}</Tag>
                    {shift.specialtyCode && <Tag>{shift.specialtyCode}</Tag>}
                </>
            ),
        },
        {
            title: 'Scheduled Time',
            dataIndex: 'startTime',
            width: 320,
            render: (_: any, shift: PayrollShiftListItemDto) => {
                const tz = shift.facility?.timezone;
                const start = DateTime.fromJSDate(shift.startTime);
                const end = DateTime.fromJSDate(shift.endTime);
                return (
                    <Typography.Text type="secondary">
                        {(tz ? start.setZone(tz) : start).toLocaleString(
                            HumlaDateTime.DATETIME_SHORT
                        )}{' '}
                        -{' '}
                        {(tz ? end.setZone(tz) : end).toLocaleString(
                            tz
                                ? HumlaDateTime.DATETIME_SHORT_WITH_TZ
                                : HumlaDateTime.DATETIME_SHORT
                        )}
                    </Typography.Text>
                );
            },
        },
        {
            title: 'Assigned Nurse',
            dataIndex: 'nurseProfile',
            render: (
                nurseProfile: PayrollShiftListItemDto['nurseProfile'] | null
            ) =>
                nurseProfile && (
                    <Button
                        type="link"
                        target="_blank"
                        href={`/profiles/${nurseProfile.id}`}
                        style={{ padding: 0, height: 'auto' }}
                    >
                        {nurseProfile.firstName} {nurseProfile.lastName}
                    </Button>
                ),
        },
        {
            title: 'Hours Submitted',
            dataIndex: 'minutesWorked',
            width: 140,
            render: (
                minutesWorked: number | null,
                shift: PayrollShiftListItemDto
            ) => {
                const payPeriod = payPeriodForShift(shift);

                if (shift.didNotWork) {
                    return <b>Did not work</b>;
                }
                if (!minutesWorked && !shift.onCallShift?.minutesWorked) {
                    return (
                        <Typography.Text type="secondary">
                            Not submitted
                        </Typography.Text>
                    );
                }

                const totalMinutesSubmitted =
                    (minutesWorked || 0) +
                    (shift.onCallShift?.minutesWorked || 0);
                const payPeriodMinutes =
                    payPeriod &&
                    (payPeriod.minutesWorked || payPeriod.onCallMinutesWorked)
                        ? (payPeriod.minutesWorked || 0) +
                          (payPeriod.onCallMinutesWorked || 0)
                        : null;

                if (
                    payPeriodMinutes &&
                    payPeriodMinutes < totalMinutesSubmitted
                ) {
                    return `${toHourAndMinuteDisplay(payPeriodMinutes, true)} / ${toHourAndMinuteDisplay(totalMinutesSubmitted, true)}`;
                }

                if (payPeriodMinutes) {
                    return toHourAndMinuteDisplay(payPeriodMinutes, true);
                }

                return toHourAndMinuteDisplay(totalMinutesSubmitted, true);
            },
        },

        {
            title: 'Hours Scheduled',
            width: 140,
            render: (_: any, shift: PayrollShiftListItemDto) => {
                const startTime = DateTime.fromJSDate(shift.startTime);
                const endTime = DateTime.fromJSDate(shift.endTime);
                const scheduledMinutes = endTime.diff(
                    startTime,
                    'minutes'
                ).minutes;
                return toHourAndMinuteDisplay(scheduledMinutes, true);
            },
        },
        {
            title: 'In Stripe',
            width: 80,
            render: (_: any, shift: PayrollShiftListItemDto) =>
                payPeriodForShift(shift)?.stripeInvoiceId && (
                    <CheckCircleOutlined />
                ),
        },
        {
            title: 'In Zeal',
            width: 80,
            render: (_: any, shift: PayrollShiftListItemDto) => {
                if (shift.nurseProfile?.isSalaried) {
                    return (
                        <Typography.Text type="secondary">
                            (Salaried)
                        </Typography.Text>
                    );
                }
                return (
                    payPeriodForShift(shift)?.payrollEmployeeCheckId && (
                        <CheckCircleOutlined />
                    )
                );
            },
        },
    ];

    return (
        <>
            <ShiftSummary shifts={shifts} />
            <Card size="small">
                <AntTable<PayrollShiftListItemDto>
                    size="small"
                    columns={columns}
                    dataSource={shifts}
                    rowKey="id"
                    pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} shifts`,
                    }}
                />
            </Card>
        </>
    );
}
